import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { 
  Send, 
  FileText, 
  DollarSign, 
  Users, 
  Calendar,
  Target,
  BookOpen,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface Course {
  id: string;
  title: string;
  description: string;
  duration_hours?: number;
  max_participants?: number;
  is_online: boolean;
  location?: string;
}

interface CourseProposalFormProps {
  course: Course;
  onSuccess: () => void;
}

export const CourseProposalForm: React.FC<CourseProposalFormProps> = ({ course, onSuccess }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [existingProposal, setExistingProposal] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    justification: '',
    learning_objectives: [''],
    target_audience: [] as string[],
    materials_needed: '',
    budget_estimate: course.duration_hours ? course.duration_hours * 100 : 500, // Estimate $100 per hour
    subject_area: '',
    proficiency_level: 'intermediate',
    teaching_standards: [] as string[],
    target_departments: [] as string[],
    target_job_types: [] as string[],
    target_grade_levels: [] as string[],
    instructor_preference: '',
    proposed_dates: {
      preferred_start: '',
      preferred_end: '',
      flexibility: 'flexible'
    }
  });

  const targetAudienceOptions = [
    'New Teachers (0-3 years)',
    'Experienced Teachers (4-10 years)', 
    'Veteran Teachers (10+ years)',
    'Administrators',
    'Support Staff',
    'All Staff'
  ];

  const subjectAreas = [
    'Mathematics',
    'English Language Arts',
    'Science',
    'Social Studies',
    'Arts',
    'Physical Education',
    'Special Education',
    'Technology',
    'Professional Development',
    'Leadership'
  ];

  const departments = [
    'Elementary Education',
    'Secondary Education',
    'Special Education',
    'Administration',
    'Counseling',
    'Library/Media',
    'Technology',
    'Maintenance',
    'Transportation',
    'Food Services'
  ];

  useEffect(() => {
    checkExistingProposal();
  }, [course.id]);

  const checkExistingProposal = async () => {
    try {
      const { data, error } = await supabase
        .from('course_proposals')
        .select('*')
        .eq('course_id', course.id)
        .eq('proposed_by', user?.id)
        .single();

      if (!error && data) {
        setExistingProposal(data);
        
        const parsedDates = typeof data.proposed_dates === 'string' 
          ? JSON.parse(data.proposed_dates) 
          : data.proposed_dates || formData.proposed_dates;
        
        // Pre-fill form with existing data
        setFormData({
          ...formData,
          justification: data.justification || '',
          learning_objectives: data.learning_objectives || [''],
          target_audience: data.target_audience || [],
          materials_needed: data.materials_needed || '',
          budget_estimate: data.budget_estimate || formData.budget_estimate,
          subject_area: data.subject_area || '',
          proficiency_level: (data.proficiency_level as 'beginner' | 'intermediate' | 'advanced' | 'all_levels') || 'intermediate',
          teaching_standards: data.teaching_standards || [],
          target_departments: data.target_departments || [],
          target_job_types: data.target_job_types || [],
          target_grade_levels: data.target_grade_levels || [],
          instructor_preference: data.instructor_preference || '',
          proposed_dates: parsedDates
        });
      }
    } catch (error) {
      // No existing proposal found, continue with empty form
    }
  };

  const handleSubmit = async () => {
    if (!formData.justification.trim() || !formData.subject_area) {
      toast({
        title: "Missing Information",
        description: "Please fill in the justification and subject area",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      // Get user profile for district_id
      const { data: profile } = await supabase
        .from('profiles')
        .select('district_id, first_name, last_name')
        .eq('id', user?.id)
        .single();

      const proposalData = {
        course_id: course.id,
        title: course.title,
        description: course.description,
        duration_hours: course.duration_hours,
        max_participants: course.max_participants,
        is_online: course.is_online,
        location: course.location,
        proposed_by: user?.id,
        district_id: profile?.district_id,
        proposer_name: `${profile?.first_name} ${profile?.last_name}`,
        status: 'submitted',
        ...formData
      };

      let result;
      if (existingProposal) {
        // Update existing proposal
        result = await supabase
          .from('course_proposals')
          .update(proposalData)
          .eq('id', existingProposal.id);
      } else {
        // Create new proposal
        result = await supabase
          .from('course_proposals')
          .insert(proposalData);
      }

      if (result.error) throw result.error;

      toast({
        title: "Proposal Submitted",
        description: `Course proposal ${existingProposal ? 'updated' : 'submitted'} successfully for administrative review`
      });

      onSuccess();
    } catch (error) {
      console.error('Error submitting proposal:', error);
      toast({
        title: "Error",
        description: "Failed to submit proposal. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const addLearningObjective = () => {
    setFormData({
      ...formData,
      learning_objectives: [...formData.learning_objectives, '']
    });
  };

  const updateLearningObjective = (index: number, value: string) => {
    const updated = [...formData.learning_objectives];
    updated[index] = value;
    setFormData({ ...formData, learning_objectives: updated });
  };

  const removeLearningObjective = (index: number) => {
    const updated = formData.learning_objectives.filter((_, i) => i !== index);
    setFormData({ ...formData, learning_objectives: updated });
  };

  const toggleArrayValue = (array: string[], value: string, setter: (values: string[]) => void) => {
    if (array.includes(value)) {
      setter(array.filter(item => item !== value));
    } else {
      setter([...array, value]);
    }
  };

  if (existingProposal && existingProposal.status === 'approved') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-green-600 flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Proposal Approved
          </CardTitle>
          <CardDescription>
            Your course proposal has been approved and is ready for learners!
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <p><strong>Status:</strong> <Badge variant="secondary" className="bg-green-100 text-green-800">Approved</Badge></p>
            <p><strong>Reviewed:</strong> {new Date(existingProposal.reviewed_at).toLocaleDateString()}</p>
            {existingProposal.review_notes && (
              <p><strong>Admin Notes:</strong> {existingProposal.review_notes}</p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <div className="p-3 rounded-lg bg-primary text-primary-foreground">
          <Send className="h-6 w-6" />
        </div>
        <div>
          <h3 className="text-xl font-bold">Submit Course Proposal</h3>
          <p className="text-muted-foreground">
            Request administrative approval for "{course.title}"
          </p>
        </div>
      </div>

      {existingProposal && (
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-blue-800 flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Existing Proposal Found
            </CardTitle>
            <CardDescription>
              You have already submitted a proposal for this course. 
              Status: <Badge variant="outline">{existingProposal.status}</Badge>
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {/* Course Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Course Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <p><strong>Title:</strong> {course.title}</p>
            <p><strong>Description:</strong> {course.description}</p>
            <p><strong>Format:</strong> {course.is_online ? 'Online' : 'In-Person'}</p>
          </div>
          <div className="space-y-2">
            {course.duration_hours && <p><strong>Duration:</strong> {course.duration_hours} hours</p>}
            {course.max_participants && <p><strong>Max Participants:</strong> {course.max_participants}</p>}
            {course.location && <p><strong>Location:</strong> {course.location}</p>}
          </div>
        </CardContent>
      </Card>

      {/* Proposal Form */}
      <Card>
        <CardHeader>
          <CardTitle>Proposal Details</CardTitle>
          <CardDescription>
            Provide additional information to support your course proposal
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Justification */}
          <div>
            <Label htmlFor="justification">Justification & Need *</Label>
            <Textarea
              id="justification"
              value={formData.justification}
              onChange={(e) => setFormData({ ...formData, justification: e.target.value })}
              placeholder="Explain why this course is needed and how it will benefit learners..."
              rows={4}
              className="mt-1"
            />
          </div>

          {/* Subject Area */}
          <div>
            <Label>Subject Area *</Label>
            <Select value={formData.subject_area} onValueChange={(value) => setFormData({ ...formData, subject_area: value })}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select subject area" />
              </SelectTrigger>
              <SelectContent>
                {subjectAreas.map(subject => (
                  <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Learning Objectives */}
          <div>
            <Label>Learning Objectives</Label>
            <div className="space-y-2 mt-1">
              {formData.learning_objectives.map((objective, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={objective}
                    onChange={(e) => updateLearningObjective(index, e.target.value)}
                    placeholder={`Learning objective ${index + 1}`}
                  />
                  {formData.learning_objectives.length > 1 && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => removeLearningObjective(index)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                onClick={addLearningObjective}
              >
                Add Objective
              </Button>
            </div>
          </div>

          {/* Target Audience */}
          <div>
            <Label>Target Audience</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {targetAudienceOptions.map(audience => (
                <div key={audience} className="flex items-center space-x-2">
                  <Checkbox
                    id={audience}
                    checked={formData.target_audience.includes(audience)}
                    onCheckedChange={() => 
                      toggleArrayValue(formData.target_audience, audience, 
                        (values) => setFormData({ ...formData, target_audience: values }))
                    }
                  />
                  <Label htmlFor={audience} className="text-sm">{audience}</Label>
                </div>
              ))}
            </div>
          </div>

          {/* Budget and Materials */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="budget">Estimated Budget ($)</Label>
              <Input
                id="budget"
                type="number"
                value={formData.budget_estimate}
                onChange={(e) => setFormData({ ...formData, budget_estimate: Number(e.target.value) })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Proficiency Level</Label>
              <Select value={formData.proficiency_level} onValueChange={(value) => setFormData({ ...formData, proficiency_level: value })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                  <SelectItem value="all_levels">All Levels</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Materials Needed */}
          <div>
            <Label htmlFor="materials">Materials & Resources Needed</Label>
            <Textarea
              id="materials"
              value={formData.materials_needed}
              onChange={(e) => setFormData({ ...formData, materials_needed: e.target.value })}
              placeholder="List any materials, technology, or resources needed for this course..."
              rows={3}
              className="mt-1"
            />
          </div>

          {/* Target Departments */}
          <div>
            <Label>Target Departments</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {departments.map(dept => (
                <div key={dept} className="flex items-center space-x-2">
                  <Checkbox
                    id={dept}
                    checked={formData.target_departments.includes(dept)}
                    onCheckedChange={() => 
                      toggleArrayValue(formData.target_departments, dept, 
                        (values) => setFormData({ ...formData, target_departments: values }))
                    }
                  />
                  <Label htmlFor={dept} className="text-sm">{dept}</Label>
                </div>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !formData.justification.trim() || !formData.subject_area}
            className="w-full"
            size="lg"
          >
            <Send className="h-4 w-4 mr-2" />
            {loading ? 'Submitting...' : existingProposal ? 'Update Proposal' : 'Submit Proposal'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};